import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "./Header";

type FormState = {
  nombre: string;
  empresa: string;
  cargo: string;
  interes: string;
  telefono: string;
  email: string;
  consentimiento: boolean;
};

const CONSENT_TEXT =
  "Autorizo a Azul Profundo SpA (RUT 78.022.067-8), titular de la marca Risk594, a tratar los datos personales que entrego en este formulario con la finalidad única de responder mi consulta comercial. Entiendo que puedo revocar este consentimiento en cualquier momento escribiendo a contacto@risk594.com. Base legal: Ley 19.628 art. 2 letra g (consentimiento expreso) + Ley 21.719 art. 12 (vigente desde 1-dic-2026).";

export function Form() {
  const [state, setState] = useState<FormState>({
    nombre: "",
    empresa: "",
    cargo: "",
    interes: "",
    telefono: "",
    email: "",
    consentimiento: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const utm = (() => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get("utm_source") ?? "",
      utm_medium: params.get("utm_medium") ?? "",
      utm_campaign: params.get("utm_campaign") ?? "",
    };
  })();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!state.consentimiento) {
      setError("Debes aceptar el consentimiento para enviar.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await fetch("/api/v1/comercial/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...state,
          ...utm,
          user_agent: navigator.userAgent.slice(0, 500),
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({ detail: "unknown" }));
        throw new Error(
          typeof body.detail === "string" ? body.detail : `HTTP ${resp.status}`
        );
      }
      const { token } = (await resp.json()) as { token: string };
      navigate(`/gracias/${encodeURIComponent(token)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error enviando el formulario.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="container">
      <Header />
      <h1>Hablemos de higiene ocupacional.</h1>
      <p className="lead">
        Risk594 ayuda a contratistas mineros a cerrar la brecha entre exposición, vigilancia médica y
        acreditación. Déjanos tus datos y te contactamos por WhatsApp en menos de 24 horas.
      </p>

      <form className="card" onSubmit={onSubmit} noValidate>
        {error && <div className="err">{error}</div>}

        <div className="field">
          <label htmlFor="nombre">
            Nombre completo<span className="req">*</span>
          </label>
          <input
            id="nombre"
            type="text"
            required
            maxLength={200}
            value={state.nombre}
            onChange={(e) => setState({ ...state, nombre: e.target.value })}
          />
        </div>

        <div className="field">
          <label htmlFor="empresa">
            Empresa<span className="req">*</span>
          </label>
          <input
            id="empresa"
            type="text"
            required
            maxLength={200}
            value={state.empresa}
            onChange={(e) => setState({ ...state, empresa: e.target.value })}
          />
        </div>

        <div className="field">
          <label htmlFor="cargo">
            Cargo<span className="req">*</span>
          </label>
          <input
            id="cargo"
            type="text"
            required
            maxLength={200}
            value={state.cargo}
            onChange={(e) => setState({ ...state, cargo: e.target.value })}
          />
        </div>

        <div className="field">
          <label htmlFor="interes">
            ¿En qué te podemos ayudar?<span className="req">*</span>
          </label>
          <textarea
            id="interes"
            required
            maxLength={500}
            value={state.interes}
            onChange={(e) => setState({ ...state, interes: e.target.value })}
            placeholder="Ej: necesitamos cerrar el programa EPR del contrato con BHP antes de fin de mes."
          />
        </div>

        <div className="field">
          <label htmlFor="telefono">Teléfono (opcional)</label>
          <input
            id="telefono"
            type="tel"
            maxLength={16}
            value={state.telefono}
            onChange={(e) => setState({ ...state, telefono: e.target.value })}
            placeholder="+56 9 XXXX XXXX"
          />
        </div>

        <div className="field">
          <label htmlFor="email">Email (opcional)</label>
          <input
            id="email"
            type="email"
            maxLength={256}
            value={state.email}
            onChange={(e) => setState({ ...state, email: e.target.value })}
          />
        </div>

        <label className="consent">
          <input
            type="checkbox"
            checked={state.consentimiento}
            onChange={(e) => setState({ ...state, consentimiento: e.target.checked })}
            required
          />
          <p>{CONSENT_TEXT}</p>
        </label>

        <button type="submit" className="submit" disabled={submitting}>
          {submitting ? "Enviando..." : "Enviar y abrir WhatsApp"}
        </button>
      </form>

      <footer className="legal">
        Azul Profundo SpA · RUT 78.022.067-8 · Marca Risk594
        <br />
        Para contacto directo: <a href="mailto:contacto@risk594.com">contacto@risk594.com</a>
      </footer>
    </div>
  );
}
